import { Button, type ButtonProps } from '@ctw/shared/components/button';
import { Tooltip, type TooltipProps } from '@ctw/shared/components/tooltip';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { tw } from '@ctw/shared/utils/tailwind';
import { faAdd } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon, type FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import {
  type Dispatch,
  type FunctionComponent,
  type MouseEvent,
  type SetStateAction,
  useCallback,
} from 'react';

export type RiskGapAddToNoteButtonProps = {
  /** The text we will append within the addTextCallback action.  */
  textToAdd: string;
  /** The callback controlling the string state that we want to append to. */
  addTextCallback: Dispatch<SetStateAction<string | undefined | null>>;
  buttonClassName?: string;
};

/**
 * A button that appends the textToAdd prop to a shared text state via the addTextCallback.
 * Used only in the v2 risk interface.
 */
export const RiskGapAddToNoteButton: FunctionComponent<RiskGapAddToNoteButtonProps> = ({
  textToAdd,
  addTextCallback,
  buttonClassName = tw`shrink h-8 w-8 rounded-md cursor-pointer opacity-0 transition-opacity group-hover:opacity-100`,
}) => {
  const telemetry = useTelemetry();
  const handleClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      // stop propagation so we don't simultaneously trigger nested cards
      event.stopPropagation();
      addTextCallback((prevState: string | null | undefined) => {
        const newText = prevState ? prevState : '';
        if (newText.endsWith(textToAdd)) {
          // redundant clicks do not add the same content twice.
          return newText;
        }
        if (newText.length > 0) {
          // add newlines between existing text
          return `${newText}\n${textToAdd}`;
        }
        // skip adding a new line to a blank note
        return textToAdd;
      });
      telemetry.trackInteraction('risk_gap_add_to_note_button_click');
    },
    [textToAdd, addTextCallback, telemetry],
  );

  return (
    <RiskGapAddToNoteButtonComponent onClick={handleClick} buttonClassName={buttonClassName} />
  );
};

export type RiskGapAddToNoteButtonComponentProps = {
  // we allow content and id to be optional because we provide defaults in the CopyButton
  [K in keyof Pick<TooltipProps, 'content' | 'id'> as `tooltip${Capitalize<K>}`]?: TooltipProps[K];
} & {
  [K in keyof Pick<
    ButtonProps,
    'ariaLabel' | 'className' | 'telemetryObfuscatedIdentifier'
  > as `button${Capitalize<K>}`]: ButtonProps[K];
} & {
  [K in keyof Pick<
    FontAwesomeIconProps,
    'className'
  > as `icon${Capitalize<K>}`]: FontAwesomeIconProps[K];
  // Common conventions dictate that we assign core behavior to certain child components.
} & { onClick: ButtonProps['onClick'] };

/** A display only component that relies on minimal context and hook state. */
export const RiskGapAddToNoteButtonComponent: FunctionComponent<
  RiskGapAddToNoteButtonComponentProps
> = ({
  tooltipId = 'add-to-note',
  tooltipContent = 'Add to note',
  onClick,
  buttonClassName = tw`h-8 w-8 rounded-md cursor-pointer opacity-0 transition-opacity group-hover:opacity-100`,
  buttonAriaLabel = 'Add to note',
  buttonTelemetryObfuscatedIdentifier = 'ADD TO NOTES',
}) => (
  <Button
    onClick={onClick}
    type="button"
    ariaLabel={buttonAriaLabel}
    size={'sm'}
    variant="unstyled"
    telemetryObfuscatedIdentifier={buttonTelemetryObfuscatedIdentifier}
    className={buttonClassName}
  >
    <Tooltip id={tooltipId} content={tooltipContent}>
      <FontAwesomeIcon icon={faAdd} className={tw`h-4 w-4 text-muted-foreground`} />
    </Tooltip>
  </Button>
);
